/**
 * New user — own tab (big form: identity, credentials, role, student-level details).
 * Shared by super admin (/super-admin/users/new), tenant admin and instructor (/admin/users/new);
 * role-aware via useAuth():
 *   super_admin   → tenant select + all roles (learner / instructor / tenant admin)
 *   tenant_admin  → instructor / learner
 *   instructor    → learner only (role locked)
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../components/DashboardLayout';
import { useTabs, useTabLabel } from '../../components/shell/TabsContext';
import RecordPage, { FormSection } from '../../components/shell/RecordPage';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import PasswordField, { RequireChangeCheckbox } from '../../components/shell/PasswordField';
import { FloppyDisk, User, Key, Student, Buildings } from '@phosphor-icons/react';
import { toast } from 'sonner';

const EMPTY_USER = {
  email: '',
  phone: '',
  password: '',
  must_change_password: true,
  first_name: '',
  last_name: '',
  role: 'learner',
  tenant_id: '',
  guardian_name: '',
  student_level: '',
  member_id: '',
  child_ids: [],
  school_class: '',
  school_name: '',
  school_board: '',
  college_year: '',
  college_name: '',
  professional_course: '',
  professional_level: '',
  competitive_exam: ''
};

const LIST_ROUTE = {
  super_admin: '/super-admin/users',
  tenant_admin: '/admin/users',
  instructor: '/instructor/students',
};

export default function UserForm() {
  const { api, user: currentUser } = useAuth();
  const navigate = useNavigate();
  const { closeAndNavigate } = useTabs();
  useTabLabel('New user');

  const role = currentUser?.role;
  const isSuperAdmin = role === 'super_admin';
  const isInstructor = role === 'instructor';
  const listRoute = LIST_ROUTE[role] || '/';

  const formRef = useRef(null);
  const [newUser, setNewUser] = useState({ ...EMPTY_USER });
  const [tenants, setTenants] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  // A parent account is only useful once it is linked to the children it may see.
  const [learners, setLearners] = useState([]);

  useEffect(() => {
    if (newUser.role !== 'parent') return;
    const tenantId = isSuperAdmin ? newUser.tenant_id : currentUser?.tenant_id;
    if (!tenantId) { setLearners([]); return; }
    api.get(`/users?role=learner${isSuperAdmin ? `&tenant_id=${tenantId}` : ''}`)
      .then((res) => setLearners((res.data || []).filter((u) => u.tenant_id === tenantId)))
      .catch(() => setLearners([]));
  }, [api, newUser.role, newUser.tenant_id, isSuperAdmin, currentUser?.tenant_id]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    api.get('/tenants')
      .then((res) => setTenants(res.data || []))
      .catch(() => setTenants([]));
  }, [api, isSuperAdmin]);

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Strip empty strings so backend doesn't reject empty email as invalid.
      // tenant_id: super admin picks it in the form; everyone else creates inside their own tenant.
      const payload = Object.fromEntries(
        Object.entries({ ...newUser, tenant_id: isSuperAdmin ? newUser.tenant_id : currentUser.tenant_id })
          .filter(([, v]) => v !== '' && v !== undefined)
      );
      if (payload.role === 'parent') {
        payload.child_ids = newUser.child_ids || [];
        if (!payload.child_ids.length) {
          toast.error('Pick at least one student for this parent to follow.');
          setSubmitting(false);
          return;
        }
      } else {
        delete payload.child_ids;
      }
      if (!payload.email && !payload.phone) {
        toast.error('Please provide either Email or Phone');
        setSubmitting(false);
        return;
      }
      await api.post('/auth/register', payload);
      toast.success('User created successfully');
      setNewUser({ ...EMPTY_USER });
      closeAndNavigate('user:new', listRoute);
    } catch (error) {
      const detail = error.response?.data?.detail;
      const msg = typeof detail === 'string'
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => `${d.loc?.slice(-1)[0] || 'field'}: ${d.msg}`).join('; ')
          : 'Failed to create user';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // Toolbar "Create user" submits the <form> so native validation (required / minLength) still runs
  const submitForm = () => {
    const form = formRef.current;
    if (!form) return;
    if (typeof form.requestSubmit === 'function') form.requestSubmit();
    else form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
  };

  const roleOptions = isSuperAdmin
    ? [['learner', 'Learner'], ['instructor', 'Instructor'], ['parent', 'Parent'], ['tenant_admin', 'Tenant Admin']]
    : isInstructor
      ? [['learner', 'Learner']]
      : [['learner', 'Learner'], ['instructor', 'Instructor'], ['parent', 'Parent']];

  return (
    <DashboardLayout>
      <RecordPage
        data-testid="user-form"
        title="New user"
        subtitle={isSuperAdmin ? 'Add a new user to the platform' : 'Create a new user in your organization'}
        onBack={() => navigate(listRoute)}
       
        primary={[
          { key: 'save', label: submitting ? 'Creating…' : 'Create user', icon: FloppyDisk, onClick: submitForm, disabled: submitting, testid: 'save-user-btn' },
          { key: 'cancel', label: 'Cancel', variant: 'outline', onClick: () => navigate(listRoute), testid: 'cancel-user-btn' },
        ]}
      >
        <form ref={formRef} onSubmit={handleCreateUser} className="space-y-4" data-testid="create-user-form">
          <FormSection title="Identity" icon={User}>
            <div className="form-grid">
              <div className="field">
                <Label htmlFor="first_name" className="text-xs">First Name *</Label>
                <Input
                  id="first_name"
                  value={newUser.first_name}
                  onChange={(e) => setNewUser({ ...newUser, first_name: e.target.value })}
                  required
                 
                />
              </div>
              <div className="field">
                <Label htmlFor="last_name" className="text-xs">Last Name *</Label>
                <Input
                  id="last_name"
                  value={newUser.last_name}
                  onChange={(e) => setNewUser({ ...newUser, last_name: e.target.value })}
                  required
                 
                />
              </div>
              <div className="field">
                <Label htmlFor="member_id" className="text-xs">ID</Label>
                <Input
                  id="member_id"
                  value={newUser.member_id}
                  onChange={(e) => setNewUser({ ...newUser, member_id: e.target.value })}
                  placeholder={newUser.role === 'instructor' ? 'INS-0001' : newUser.role === 'parent' ? 'PAR-0001' : 'STU-0001'}
                  data-testid="member-id-input"
                />
                <p className="field-hint">Leave blank and the next free ID is issued. Must be unique in this organisation.</p>
              </div>
              <div className="field">
                <Label htmlFor="email" className="text-xs">Email{isSuperAdmin ? ' *' : ''}</Label>
                <Input
                  id="email"
                  type="email"
                  value={newUser.email}
                  onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                  required={isSuperAdmin}
                 
                />
              </div>
              <div className="field">
                <Label htmlFor="phone" className="text-xs">Phone</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={newUser.phone}
                  onChange={(e) => setNewUser({ ...newUser, phone: e.target.value })}
                 
                />
              </div>
            </div>
            {!isSuperAdmin && (
              <p className="text-xs text-muted-foreground mt-2">Provide at least one of Email or Phone.</p>
            )}
          </FormSection>

          <FormSection title="Access" icon={Key}>
            <div className={`grid grid-cols-1 gap-3 ${isSuperAdmin ? 'md:grid-cols-3' : 'md:grid-cols-2'}`}>
              <PasswordField
                id="password"
                label="Password"
                required
                generate
                value={newUser.password}
                onChange={(v) => setNewUser({ ...newUser, password: v })}
                placeholder="At least 6 characters"
                testid="password-input"
              />
              <div className="field">
                <Label htmlFor="role" className="text-xs">Role *</Label>
                {isInstructor ? (
                  <Input value="Learner" disabled className="bg-muted" data-testid="role-locked" />
                ) : (
                  <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                    <SelectTrigger data-testid="role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {roleOptions.map(([v, l]) => (
                        <SelectItem key={v} value={v}>{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {isSuperAdmin && (
                <div className="field">
                  <Label htmlFor="tenant" className="text-xs">Tenant</Label>
                  <Select value={newUser.tenant_id} onValueChange={(v) => setNewUser({ ...newUser, tenant_id: v })}>
                    <SelectTrigger data-testid="tenant-select">
                      <SelectValue placeholder="Select tenant" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map(t => (
                        <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <div className="mt-3 max-w-md">
              <RequireChangeCheckbox
                checked={newUser.must_change_password}
                onCheckedChange={(v) => setNewUser({ ...newUser, must_change_password: v })}
                testid="new-user-require-change"
              />
            </div>
          </FormSection>

          {/* A parent's children — what their Student Diary will show */}
          {newUser.role === 'parent' && (
            <FormSection title="Children" icon={Student} description="The students this parent can follow in the Student Diary. They see nobody else.">
              {learners.length === 0 ? (
                <p className="field-hint" data-testid="no-learners">
                  No learners in this organisation yet — create the student first, then the parent account.
                </p>
              ) : (
                <div className="grid gap-1.5 sm:grid-cols-2 lg:grid-cols-3" data-testid="child-picker">
                  {learners.map((l) => {
                    const picked = (newUser.child_ids || []).includes(l.id);
                    return (
                      <label key={l.id}
                        className={`flex items-center gap-2 rounded-md border px-2.5 h-9 cursor-pointer text-[13px] ${picked ? 'border-shell-primary bg-shell-primary/5' : 'border-border hover:bg-muted/40'}`}>
                        <input type="checkbox" checked={picked} data-testid={`child-${l.id}`}
                          onChange={() => setNewUser({
                            ...newUser,
                            child_ids: picked
                              ? (newUser.child_ids || []).filter((id) => id !== l.id)
                              : [...(newUser.child_ids || []), l.id],
                          })} />
                        <span className="truncate">{l.first_name} {l.last_name}</span>
                      </label>
                    );
                  })}
                </div>
              )}
            </FormSection>
          )}

          {/* Learner-specific fields */}
          {newUser.role === 'learner' && (
            <FormSection title="Student details" icon={Student} description="Optional — guardian and study level for learners.">
              <div className="form-grid">
                <div className="field">
                  <Label className="text-xs">Guardian Name</Label>
                  <Input value={newUser.guardian_name} onChange={(e) => setNewUser({...newUser, guardian_name: e.target.value})} />
                </div>
                <div className="field">
                  <Label className="text-xs">Student Level</Label>
                  <select className="form-select"
                    value={newUser.student_level}
                    onChange={(e) => setNewUser({...newUser, student_level: e.target.value, school_class:'', school_name:'', school_board:'', college_year:'', college_name:'', professional_course:'', professional_level:'', competitive_exam:''})}
                    data-testid="student-level-select">
                    <option value="">Select Level...</option>
                    <option value="school">School</option>
                    <option value="college">College</option>
                    <option value="competitive">Competitive</option>
                    <option value="professional">Professional</option>
                  </select>
                </div>
              </div>
              {newUser.student_level === 'school' && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 border rounded bg-muted/30 mt-3">
                  <select className="form-select-inline" value={newUser.school_class} onChange={(e) => setNewUser({...newUser, school_class: e.target.value})}>
                    <option value="">Class</option>
                    {Array.from({length:12},(_,i)=>i+1).map(n => <option key={n} value={String(n)}>Class {n}</option>)}
                  </select>
                  <select className="form-select-inline" value={newUser.school_board} onChange={(e) => setNewUser({...newUser, school_board: e.target.value})}>
                    <option value="">Board</option>
                    <option value="CBSE">CBSE</option><option value="SSC">SSC</option><option value="IGSE">IGSE</option><option value="ICSE">ICSE</option><option value="Others">Others</option>
                  </select>
                  <Input placeholder="School Name" value={newUser.school_name} onChange={(e) => setNewUser({...newUser, school_name: e.target.value})} />
                </div>
              )}
              {newUser.student_level === 'college' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded bg-muted/30 mt-3">
                  <select className="form-select-inline" value={newUser.college_year} onChange={(e) => setNewUser({...newUser, college_year: e.target.value})}>
                    <option value="">Year</option>
                    <option value="Inter 1st Year">Inter 1st Year</option><option value="Inter 2nd Year">Inter 2nd Year</option>
                  </select>
                  <Input placeholder="College Name" value={newUser.college_name} onChange={(e) => setNewUser({...newUser, college_name: e.target.value})} />
                </div>
              )}
              {newUser.student_level === 'competitive' && (
                <div className="p-2 border rounded bg-muted/30 mt-3">
                  <select className="form-select" value={newUser.competitive_exam} onChange={(e) => setNewUser({...newUser, competitive_exam: e.target.value})}>
                    <option value="">Exam Type</option>
                    <option value="EAMCET">EAMCET</option><option value="MAINS">MAINS</option><option value="ADVANCED">ADVANCED</option><option value="NEET">NEET</option>
                  </select>
                </div>
              )}
              {newUser.student_level === 'professional' && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 p-2 border rounded bg-muted/30 mt-3">
                  <select className="form-select-inline" value={newUser.professional_course} onChange={(e) => setNewUser({...newUser, professional_course: e.target.value})}>
                    <option value="">Course</option>
                    <option value="CA">CA</option><option value="CMA">CMA</option><option value="CS">CS</option><option value="US CMA">US CMA</option><option value="Others">Others</option>
                  </select>
                  <select className="form-select-inline" value={newUser.professional_level} onChange={(e) => setNewUser({...newUser, professional_level: e.target.value})}>
                    <option value="">Level</option>
                    <option value="Foundation">Foundation</option><option value="Inter">Inter</option><option value="Final">Final</option><option value="Part 1">Part 1</option><option value="Part 2">Part 2</option>
                  </select>
                </div>
              )}
            </FormSection>
          )}

          {isSuperAdmin && !newUser.tenant_id && (
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Buildings className="w-3.5 h-3.5" /> No tenant selected — the user is created at platform level.
            </p>
          )}

          {/* Hidden submit so Enter in a field still submits; the toolbar "Create user" calls requestSubmit() */}
          <button type="submit" className="hidden" aria-hidden="true" tabIndex={-1} />
        </form>
      </RecordPage>
    </DashboardLayout>
  );
}
